import {
  addressIsComplete,
  addressHasPoBox,
  addressHasData,
} from './AddressFields';
import { OCCUPATION_OTHER, OCCUPATION_STATUSES } from './options';
import { filledRows, percentGroupError } from './percent';

/**
 * Per-step validation for the Personal Data Sheet.
 *
 * This is the single source of truth: `WizardPage.goNext` uses it to gate
 * navigation and `stepStatus.computeStepStatuses` uses it to colour the
 * sidebar. The other wizard apps keep two copies of their required-field
 * lists; with this many fields, those copies would drift immediately.
 */

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const has = (v) => String(v ?? '').trim().length > 0;

/**
 * A native date input accepts a five-digit year, and a client can type one
 * without noticing, so the stored `yyyy-mm-dd` needs its own sanity check.
 */
function dobError(value, label) {
  if (!has(value)) return null;
  // The year group is deliberately open-ended: a five-digit year is the whole
  // problem we are catching, so it has to match here to be reported below.
  const match = /^(\d+)-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (!match) return `${label} does not look like a valid date.`;
  const year = Number(match[1]);
  const thisYear = new Date().getFullYear();
  if (match[1].length !== 4 || year < 1900 || year > thisYear) {
    return (
      `${label} has a year of ${match[1]}. Please check it — it should be a `
      + `four-digit year between 1900 and ${thisYear}.`
    );
  }
  return null;
}

export function hasSpouse(formData) {
  return String(formData?.household?.has_spouse || '').toLowerCase() === 'yes';
}

function contactError(d, who, isSpouse) {
  if (!has(d.full_name)) return `Please enter ${who} full legal name.`;
  if (!has(d.preferred_name)) return `Please enter ${who} preferred name.`;
  if (d.known_by_other_name === 'yes' && !has(d.other_name)) {
    return 'Please tell us the other name you are known by.';
  }
  // Security answers verify whoever calls us, so they are that person's to
  // choose. We collect the spouse's separately rather than making the first
  // person invent answers on their behalf.
  if (!isSpouse) {
    if (!has(d.mothers_maiden_name)) {
      return `Please enter ${who} mother's maiden name.`;
    }
    if (!has(d.security_question)) return 'Please choose a security question.';
    if (!has(d.security_answer)) {
      return 'Please answer your security question.';
    }
  }
  if (!addressIsComplete(d.address)) {
    return `Please complete ${who} legal/home address (street, city, state and ZIP).`;
  }
  if (addressHasPoBox(d.address)) {
    return 'Schwab and Fidelity require a physical street address for your legal/home address — a PO Box cannot be used.';
  }
  if (!has(d.phone)) return `Please enter ${who} primary phone number.`;
  if (!has(d.email)) return `Please enter ${who} email address.`;
  if (!EMAIL_RE.test(String(d.email).trim())) {
    return 'That email address does not look right. Please check it.';
  }
  if (!has(d.date_of_birth)) return `Please enter ${who} date of birth.`;
  const dobMessage = dobError(d.date_of_birth, 'The date of birth');
  if (dobMessage) return dobMessage;

  const countries = [
    ['citizenship', 'citizenship_other', 'citizenship'],
    ['residence', 'residence_other', 'legal residence'],
    ['birth_country', 'birth_country_other', 'birth'],
  ];
  for (const [field, otherField, noun] of countries) {
    if (!has(d[field])) return `Please select ${who} country of ${noun}.`;
    if (d[field] === 'other' && !has(d[otherField])) {
      return `Please specify ${who} country of ${noun}.`;
    }
  }
  return null;
}

function employmentError(d, who) {
  if (!has(d.status)) return `Please select ${who} employment status.`;

  // Self-employed clients name their business rather than an employer, but it
  // lands in the same fields: Client Services needs a name and an address
  // either way, and was otherwise chasing it at Getting Started.
  if (OCCUPATION_STATUSES.includes(d.status)) {
    const noun = d.status === 'self_employed' ? 'business' : 'employer';
    if (!has(d.employer_name)) return `Please enter ${who} ${noun} name.`;
    if (!addressIsComplete(d.employer_address)) {
      return `Please complete ${who} ${noun} address (street, city, state and ZIP).`;
    }
    // Occupation is optional: it stands in for the industry question, and
    // the business name and address above already tell Client Services what
    // they were chasing. Picking "Other" is the one case that still needs a
    // follow-up, since the stored value is meaningless without it.
    if (d.occupation === OCCUPATION_OTHER && !has(d.occupation_other)) {
      return 'Please specify the occupation.';
    }
  }
  return null;
}

function complianceError(d, who) {
  if (!has(d.exchange_affiliated)) {
    return 'Please answer the question about exchange, FINRA and broker-dealer affiliation.';
  }
  if (d.exchange_affiliated === 'yes' && !has(d.exchange_affiliated_details)) {
    return 'Please tell us the firm name and your relationship to it.';
  }
  if (!has(d.public_company_insider)) {
    return 'Please answer the question about being a director or 10% shareholder.';
  }
  if (d.public_company_insider === 'yes' && !has(d.public_company_insider_details)) {
    return 'Please tell us the company name and its ticker symbol.';
  }
  if (!has(d.paperless_delivery)) {
    return 'Please tell us how you would like to receive regulatory documents.';
  }
  if (!has(d.quarterly_reports_portal)) {
    return `Please tell us how ${who === 'your' ? 'you' : 'your spouse'} would like to receive quarterly reports.`;
  }
  if (!has(d.communication_frequency)) {
    return 'Please tell us how often you would like to hear from us.';
  }
  return null;
}

function tcpError(d) {
  if (!has(d.full_name)) {
    return "Please enter your Trusted Contact Person's full name.";
  }
  if (!has(d.relationship)) {
    return 'Please tell us your relationship to your Trusted Contact Person.';
  }
  if (!has(d.phone)) {
    return "Please enter your Trusted Contact Person's phone number.";
  }
  if (!has(d.email)) {
    return "Please enter your Trusted Contact Person's email address.";
  }
  if (!EMAIL_RE.test(String(d.email).trim())) {
    return "That Trusted Contact email address does not look right. Please check it.";
  }
  if (!addressIsComplete(d.address)) {
    return "Please complete your Trusted Contact Person's physical address.";
  }
  if (addressHasPoBox(d.address)) {
    return 'Schwab and Fidelity do not accept a PO Box for a Trusted Contact Person — please enter a physical street address.';
  }
  return secondaryTcpError(d);
}

/**
 * True once anything has been typed into the secondary Trusted Contact.
 *
 * Shared with `StepTrustedContact` so a record saved before the opt-in
 * checkbox existed still shows the data it already holds.
 */
export function secondaryTcpStarted(secondary) {
  const d = secondary || {};
  return ['full_name', 'relationship', 'phone', 'email']
    .some((k) => has(d[k])) || addressHasData(d.address);
}

/**
 * The secondary Trusted Contact is opt-in, but not half-in: once the client
 * asks for one, it is a real designation the custodian will act on, so every
 * field is required exactly as it is for the first one.
 */
function secondaryTcpError(tcp) {
  const wants = tcp.wants_secondary_tcp === true;
  const d = tcp.secondary || {};
  if (!wants && !secondaryTcpStarted(d)) return null;

  if (!has(d.full_name)) {
    return "Please enter your secondary Trusted Contact Person's full name.";
  }
  if (!has(d.relationship)) {
    return 'Please tell us your relationship to your secondary Trusted Contact Person.';
  }
  if (!has(d.phone)) {
    return "Please enter your secondary Trusted Contact Person's phone number.";
  }
  if (!has(d.email)) {
    return "Please enter your secondary Trusted Contact Person's email address.";
  }
  if (!EMAIL_RE.test(String(d.email).trim())) {
    return 'That secondary Trusted Contact email address does not look right. Please check it.';
  }
  if (!addressIsComplete(d.address)) {
    return "Please complete your secondary Trusted Contact Person's physical address.";
  }
  if (addressHasPoBox(d.address)) {
    return 'Schwab and Fidelity do not accept a PO Box for a Trusted Contact Person — please enter a physical street address.';
  }
  return null;
}

function beneficiariesError(d, spousePresent) {
  // The spouse copied the first person's lists wholesale; they were already
  // validated on the page they came from.
  if (d.same_as_primary === true) return null;

  const skipPrimary = spousePresent && d.spouse_is_sole_primary === 'yes';

  if (spousePresent && !has(d.spouse_is_sole_primary)) {
    return 'Please tell us whether your spouse or partner is your 100% primary beneficiary.';
  }

  if (!skipPrimary) {
    const primary = filledRows(d.primary);
    if (primary.length === 0) {
      return 'Please list at least one primary beneficiary.';
    }
    for (const row of primary) {
      if (!has(row.full_name)) {
        return 'Every primary beneficiary needs a full name.';
      }
      if (!has(row.relationship)) {
        return 'Every primary beneficiary needs a relationship.';
      }
      const rowDob = dobError(row.dob, `${row.full_name}'s date of birth`);
      if (rowDob) return rowDob;
    }
    const pctError = percentGroupError(d.primary, 'Primary');
    if (pctError) return pctError;
  }

  // Contingent beneficiaries are optional, but a partially-filled row is not.
  const contingent = filledRows(d.contingent);
  for (const row of contingent) {
    if (!has(row.full_name)) {
      return 'Every contingent beneficiary needs a full name.';
    }
    if (!has(row.relationship)) {
      return 'Every contingent beneficiary needs a relationship.';
    }
    const rowDob = dobError(row.dob, `${row.full_name}'s date of birth`);
    if (rowDob) return rowDob;
  }

  // Leaving contingent blank is allowed, but only deliberately. Returning an
  // error here is also what surfaces the question in the Review & Submit
  // "needs your attention" list.
  if (contingent.length === 0 && d.no_contingent_ack !== true) {
    return "Before you proceed… Are you sure you don't want to list any contingent beneficiaries? Tick the confirmation box, or add one.";
  }

  return percentGroupError(d.contingent, 'Contingent');
}

/**
 * Validate one step. Returns an error message, or null when the step passes.
 *
 * @param {string} stepKey  - e.g. 'contact' or 'spouse_contact'.
 * @param {object} formData - The full wizard form data.
 */
export function validateStep(stepKey, formData) {
  const isSpouse = stepKey.startsWith('spouse_');
  const base = isSpouse ? stepKey.slice('spouse_'.length) : stepKey;
  const data = formData?.[stepKey] || {};
  const who = isSpouse ? "your spouse's" : 'your';

  switch (base) {
    case 'welcome':
    case 'review':
      return null;
    case 'household':
      return has(data.has_spouse)
        ? null
        : 'Please tell us whether you have a spouse or partner.';
    case 'contact':
      return contactError(data, who, isSpouse);
    case 'employment':
      return employmentError(data, who);
    case 'compliance':
      return complianceError(data, who);
    case 'trusted_contact':
      return tcpError(data);
    case 'beneficiaries':
      return beneficiariesError(data, hasSpouse(formData));
    default:
      return null;
  }
}

/**
 * Find the first *visible* step that still has a validation error.
 * Used as a final whole-form check before submitting.
 *
 * @returns {{index: number, message: string}|null}
 */
export function firstIncompleteStep(steps, formData, isVisible) {
  for (let i = 0; i < steps.length; i += 1) {
    if (isVisible && !isVisible(steps[i], formData)) continue;
    const message = validateStep(steps[i].key, formData);
    if (message) return { index: i, message };
  }
  return null;
}
